import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {
  useAcm,
  useConfirmConfiguration,
  useDeleteAcm,
  useStartValidation,
} from 'app/aws/api';
import {CopyClipboardInputField} from 'app/components/general/CopyClipboardInputField';
import {ACM_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {CNAMEDisplay} from './CNAMEDisplay';

import type {KnowledgebaseSerializedType} from 'sweet-types';

interface Props {
  acm_hashid: string;
  kb: KnowledgebaseSerializedType;
}

export const DomainConfiguration = ({acm_hashid, kb}: Props) => {
  const {data: acm} = useAcm({url: `${ACM_API_URL}${acm_hashid}/`});
  const [startValidation, {isLoading: isValidatingAcm}] = useStartValidation();
  const [deleteAcm, {isLoading: isDeletingAcm}] = useDeleteAcm();
  const [confirmAcmConfig, {isLoading: isConfirmingAcmConfig}] =
    useConfirmConfiguration();

  const domainVerificationStatus = [
    'failed',
    'pending',
    'pending_validation',
    'cname_configured',
  ];

  const domain = `https://${acm?.domain}/`;
  const knowledgebasePubliclyAccessible = kb.enabled && !kb.private;
  const isDomainConfigAvailable = acm?.domain_cname && acm.domain_cname.value;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {acm?.domain && (
        <div className="d-flex my-4 flex-wrap align-items-end">
          <CopyClipboardInputField
            className="pl-0 col-12 col-sm my-0 flex-grow-1 pr-sm-2"
            id={'custom-domain-' + acm?.hashid}
            disabled={!knowledgebasePubliclyAccessible}
            label={
              <>
                {gt('Custom Domain')}
                {acm?.status === 'domain_configured' &&
                kb.enabled &&
                !kb.private ? (
                  <i
                    title={gt('Active')}
                    className="text-success ml-2 fa fa-check-circle"
                  ></i>
                ) : (
                  <i
                    title={gt('Disabled')}
                    className="text-danger ml-2 fa fa-times-circle"
                  ></i>
                )}
              </>
            }
            value={domain}
          />
          <a
            href={domain}
            target="_blank"
            rel="noopener noreferrer"
            className={cs('mt-2 mt-sm-0 btn btn-light', {
              disabled: !(
                acm.status == 'completed' && knowledgebasePubliclyAccessible
              ),
            })}
            onClick={(e) => e.stopPropagation()}
          >
            {gt('Visit Knowledge Base')}
          </a>
        </div>
      )}
      {knowledgebasePubliclyAccessible ? (
        <>
          {acm?.status != 'domain_configured' && (
            <>
              <h4 className="mb-2">{gt('Steps to setup custom domain')}</h4>
              <ul className="list-group mb-4 pl-0">
                <li className="list-group-item d-flex align-items-center p-2">
                  {gt('Select Your Domain')}{' '}
                  <i className="ml-auto far fa-check-circle"></i>
                </li>
                <li className="list-group-item d-flex align-items-center p-2">
                  {gt('Create and validate SSL certificate')}
                  <i
                    className={cs('ml-auto far', {
                      'fa-check-circle': isDomainConfigAvailable,
                      'fa-circle': !isDomainConfigAvailable,
                    })}
                  ></i>
                </li>
                <li className="list-group-item d-flex align-items-center p-2">
                  {gt('Direct traffic to your knowledge base')}{' '}
                  <i className={'ml-auto far fa-circle'}></i>
                </li>
              </ul>
              {!acm?.validation_cname && acm?.status != 'failed' && (
                <StyledInfoNotice className="jumbotron p-2 p-sm-4">
                  <p className="m-0">
                    {gt('Your domain setup is in progress...')}
                  </p>
                </StyledInfoNotice>
              )}
            </>
          )}
          {acm?.validation_cname &&
            domainVerificationStatus.includes(acm?.status) &&
            !isDomainConfigAvailable && (
              <>
                <StyledSuccessNotice className="jumbotron p-2 p-sm-4">
                  <CNAMEDisplay
                    title={gt('Create and validate SSL certificate')}
                    description={gt(
                      "To secure your knowledge base, we need to validate your domain ownership. Please add the following CNAME record to your domain's DNS settings."
                    )}
                    footer={gt(
                      'After updating your DNS, it may take some time to propagate. Once validated, we will create your SSL certificate and move on to the next step.'
                    )}
                    confirmText={gt('I have updated my DNS')}
                    loadingText={gt('Validating DNS records...')}
                    cname={acm.validation_cname}
                    isLoading={
                      isValidatingAcm || acm?.status == 'cname_configured'
                    }
                    isCanceling={isDeletingAcm}
                    onConfirm={() => startValidation({url: acm.url})}
                    onCancel={() => deleteAcm({url: acm.url})}
                  />
                </StyledSuccessNotice>
              </>
            )}
          {isDomainConfigAvailable && acm.status != 'domain_configured' && (
            <>
              <StyledNotice className="jumbotron p-2 p-sm-4">
                <CNAMEDisplay
                  title={gt('Direct traffic to your knowledge base')}
                  description={gt(
                    "To complete the setup of your custom domain for the knowledge base you'll need to add another CNAME record in your DNS settings."
                  )}
                  footer={gt(
                    'DNS changes may take some time to propagate. Once the CNAME record is detected your custom domain will automatically link to your knowledge base.'
                  )}
                  cname={acm.domain_cname}
                  confirmText={gt('I have updated my DNS again')}
                  loadingText={gt('Validating DNS records...')}
                  isCanceling={isDeletingAcm}
                  isLoading={
                    isConfirmingAcmConfig || acm?.status == 'completed'
                  }
                  onConfirm={() =>
                    confirmAcmConfig({
                      url: acm.url,
                    })
                  }
                  onCancel={() => deleteAcm({url: acm.url})}
                />
              </StyledNotice>
            </>
          )}
          {(acm?.status == 'cname_configured' || isValidatingAcm) && (
            <Notice
              NoticeComponent={StyledInfoNotice}
              heading={gt(
                'DNS check for %s is in progress',
                <strong>{acm?.domain}</strong>
              )}
              footerText={gt(
                "Sit tight, we're checking your DNS settings. If you haven't set up your DNS records yet, now's a great time—just follow the details above."
              )}
            />
          )}
          {(acm?.status == 'completed' || isConfirmingAcmConfig) && (
            <Notice
              NoticeComponent={StyledInfoNotice}
              heading={gt(
                'Domain propagation check for %s is in progress',
                <strong>{acm?.domain}</strong>
              )}
              footerText={gt(
                "Sit tight, we're checking your domain propagation. If you haven't set up your DNS records yet, now's a great time—just follow the details above."
              )}
            />
          )}

          {acm?.status == 'failed' &&
          !isValidatingAcm &&
          !isConfirmingAcmConfig ? (
            <Notice
              NoticeComponent={StyledFailedNotice}
              heading={
                isDomainConfigAvailable
                  ? gt(
                      'Domain propagation check for %s failed',
                      <strong>{acm?.domain}</strong>
                    )
                  : gt(
                      'DNS check for %s failed',
                      <strong>{acm?.domain}</strong>
                    )
              }
              footerText={
                acm.message ??
                gt('Please verify the CNAME information and try again.')
              }
            />
          ) : null}
        </>
      ) : (
        <p className="alert alert-danger">{gt('Custom Domain Disabled')}</p>
      )}
    </div>
  );
};

type NoticeProps = {
  NoticeComponent: typeof StyledNotice;
  heading: string;
  footerText: string;
};

const Notice = ({NoticeComponent, heading, footerText}: NoticeProps) => (
  <NoticeComponent className="jumbotron p-2 p-sm-4">
    <h4>{heading}</h4>
    <p>{footerText}</p>
  </NoticeComponent>
);

export const StyledNotice = styled.div`
  background-color: ${colors.midnightTealTransparent};
  border: 1px solid ${colors.midnightTeal};
  color: ${colors.midnightTeal};
  .header {
    flex: 1;
  }
  label {
    text-transform: capitalize;
  }
  .top-section {
    button {
      align-self: start;
      font-size: 0.9rem;
    }
  }
  @media (max-width: 575px) {
    font-size: 1rem;
    .top-section {
      h4 {
        font-size: 1.1rem;
      }
      button {
        font-size: 0.8rem;
      }
    }
  }
`;

export const StyledSuccessNotice = styled(StyledNotice)`
  background-color: ${colors.insGreen};
  border: 1px solid ${colors.insGreenOpaque};
  color: ${colors.insGreenText};
`;

export const StyledInfoNotice = styled(StyledNotice)`
  background-color: ${colors.alertBg};
  border: 1px solid ${colors.alertBorder};
  color: ${colors.alertText};
`;

export const StyledFailedNotice = styled(StyledNotice)`
  background-color: ${colors.delRedOpaque};
  border: 1px solid ${colors.delRed};
  color: ${colors.dangerText};
`;
